import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

type Lang = "en" | "hi" | "gu" | "ta" | "te";
type InvoiceStatus = "pending" | "ready" | "expired" | null;

const langOptions: { code: Lang; native: string }[] = [
  { code: "en", native: "Eng" },
  { code: "hi", native: "हिंदी" },
  { code: "gu", native: "ગુજ" },
  { code: "ta", native: "தமிழ்" },
  { code: "te", native: "తెలుగు" },
];

const t: Record<Lang, Record<string, string>> = {
  en: {
    taxInvoice: "Tax Invoice",
    checking: "Checking Invoice Status",
    checkingMsg: "Please wait while we check your invoice status…",
    expired_title: "Link Expired",
    expired_msg:
      "This invoice link has expired and is no longer valid. Please contact our customer support team for a new link or any further assistance.",
    pending_title: "Proforma Invoice Review",
    pending_subtitle: "Your proforma invoice is ready for review",
    pending_warning:
      "Once you approve and generate the final tax invoice, no changes or modifications / cancellation can be made. Please ensure all shipment details, charges, and billing information are correct before proceeding.",
    approveBtn: "Approve & Generate Tax Invoice",
    generating: "Generating Your Invoice…",
    generatingMsg: "Please wait while we prepare your final tax invoice. This may take a few moments…",
    ready_title: "Invoice Ready",
    ready_msg: "Your tax invoice has been generated successfully.",
    invoiceNo: "Invoice No",
    downloadBtn: "Download Invoice",
    contactTitle: "Need Help?",
    contactMsg: "Reach out to our customer support team",
    helpline: "Helpline",
    email: "Email",
    error_title: "Something Went Wrong",
    invalidLink: "Invalid invoice link.",
    footer: "Airfield Express Pvt. Ltd.",
  },
  hi: {
    taxInvoice: "कर चालान",
    checking: "चालान की स्थिति जाँच रहे हैं",
    checkingMsg: "कृपया प्रतीक्षा करें, हम आपके चालान की स्थिति जाँच रहे हैं…",
    expired_title: "लिंक समाप्त हो गया",
    expired_msg:
      "यह चालान लिंक समाप्त हो गया है और अब मान्य नहीं है। कृपया नई लिंक या किसी भी सहायता के लिए हमारी ग्राहक सेवा टीम से संपर्क करें।",
    pending_title: "प्रोफार्मा चालान समीक्षा",
    pending_subtitle: "आपका प्रोफार्मा चालान समीक्षा के लिए तैयार है",
    pending_warning:
      "एक बार जब आप अंतिम कर चालान को मंजूरी दे देते हैं और जनरेट कर देते हैं, तो उसके बाद कोई बदलाव या संशोधन / रद्दीकरण नहीं किया जा सकता। कृपया आगे बढ़ने से पहले सभी शिपमेंट विवरण, शुल्क और बिलिंग जानकारी की जाँच कर लें।",
    approveBtn: "मंजूरी दें और कर चालान जनरेट करें",
    generating: "आपका चालान जनरेट हो रहा है…",
    generatingMsg: "कृपया प्रतीक्षा करें, हम आपका अंतिम कर चालान तैयार कर रहे हैं…",
    ready_title: "चालान तैयार है",
    ready_msg: "आपका कर चालान सफलतापूर्वक जनरेट किया गया है।",
    invoiceNo: "चालान संख्या",
    downloadBtn: "चालान डाउनलोड करें",
    contactTitle: "मदद चाहिए?",
    contactMsg: "हमारी ग्राहक सेवा टीम से संपर्क करें",
    helpline: "हेल्पलाइन",
    email: "ईमेल",
    error_title: "कुछ गलत हो गया",
    invalidLink: "अमान्य चालान लिंक।",
    footer: "एयरफील्ड एक्सप्रेस प्रा. लि.",
  },
  gu: {
    taxInvoice: "કર ઇન્વોઇસ",
    checking: "ઇન્વોઇસની સ્થિતિ તપાસી રહ્યા છીએ",
    checkingMsg: "કૃપા કરીને રાહ જુઓ, અમે તમારા ઇન્વોઇસની સ્થિતિ તપાસી રહ્યા છીએ…",
    expired_title: "લિંક સમાપ્ત થઈ ગઈ",
    expired_msg:
      "આ ઇન્વોઇસ લિંક સમાપ્ત થઈ ગઈ છે અને હવે માન્ય નથી. કૃપા કરીને નવી લિંક અથવા કોઈપણ સહાય માટે અમારી ગ્રાહક સેવા ટીમનો સંપર્ક કરો.",
    pending_title: "પ્રોફોર્મા ઇન્વોઇસ સમીક્ષા",
    pending_subtitle: "તમારું પ્રોફોર્મા ઇન્વોઇસ સમીક્ષા માટે તૈયાર છે",
    pending_warning:
      "એકવાર તમે અંતિમ કર ઇન્વોઇસ મંજૂર કરો અને જનરેટ કરો, પછી કોઈ ફેરફાર અથવા સુધારો / રદ કરી શકાશે નહીં. કૃપા કરીને આગળ વધતા પહેલા તમામ શિપમેન્ટ વિગતો, ચાર્જ અને બિલિંગ માહિતી ચકાસો.",
    approveBtn: "મંજૂર કરો અને કર ઇન્વોઇસ જનરેટ કરો",
    generating: "તમારું ઇન્વોઇસ જનરેટ થઈ રહ્યું છે…",
    generatingMsg: "કૃપા કરીને રાહ જુઓ, અમે તમારું અંતિમ કર ઇન્વોઇસ તૈયાર કરી રહ્યા છીએ…",
    ready_title: "ઇન્વોઇસ તૈયાર છે",
    ready_msg: "તમારું કર ઇન્વોઇસ સફળતાપૂર્વક જનરેટ થયું છે.",
    invoiceNo: "ઇન્વોઇસ નંબર",
    downloadBtn: "ઇન્વોઇસ ડાઉનલોડ કરો",
    contactTitle: "મદદ જોઈએ?",
    contactMsg: "અમારી ગ્રાહક સેવા ટીમનો સંપર્ક કરો",
    helpline: "હેલ્પલાઇન",
    email: "ઈમેલ",
    error_title: "કંઈક ખોટું થયું",
    invalidLink: "અમાન્ય ઇન્વોઇસ લિંક.",
    footer: "એરફીલ્ડ એક્સપ્રેસ પ્રા. લિ.",
  },
  ta: {
    taxInvoice: "வரி விலைப்பட்டியல்",
    checking: "விலைப்பட்டியல் நிலையை சரிபார்க்கிறது",
    checkingMsg: "உங்கள் விலைப்பட்டியல் நிலையை சரிபார்க்கும்வரை காத்திருக்கவும்…",
    expired_title: "இணைப்பு காலாவதியானது",
    expired_msg:
      "இந்த விலைப்பட்டியல் இணைப்பு காலாவதியாகிவிட்டது, இனி செல்லுபடியாகாது. புதிய இணைப்பு அல்லது உதவிக்கு எங்கள் வாடிக்கையாளர் ஆதரவு குழுவைத் தொடர்பு கொள்ளவும்.",
    pending_title: "முன்மொழிவு விலைப்பட்டியல் மதிப்பாய்வு",
    pending_subtitle: "உங்கள் முன்மொழிவு விலைப்பட்டியல் மதிப்பாய்வுக்கு தயாராக உள்ளது",
    pending_warning:
      "நீங்கள் இறுதி வரி விலைப்பட்டியலை ஒப்புதல் அளித்து உருவாக்கியவுடன், அதன் பிறகு எந்த மாற்றங்களும் / ரத்து செய்யவும் இயலாது. தொடர்வதற்கு முன் அனைத்து ஷிப்பிங் விவரங்கள், கட்டணங்கள் மற்றும் பில்லிங் தகவல்களை உறுதிப்படுத்தவும்.",
    approveBtn: "ஒப்புதல் அளித்து விலைப்பட்டியல் உருவாக்கு",
    generating: "உங்கள் விலைப்பட்டியல் உருவாக்கப்படுகிறது…",
    generatingMsg: "உங்கள் இறுதி வரி விலைப்பட்டியலை தயாரிக்கும்வரை காத்திருக்கவும்…",
    ready_title: "விலைப்பட்டியல் தயார்",
    ready_msg: "உங்கள் வரி விலைப்பட்டியல் வெற்றிகரமாக உருவாக்கப்பட்டது.",
    invoiceNo: "விலைப்பட்டியல் எண்",
    downloadBtn: "விலைப்பட்டியலை பதிவிறக்கு",
    contactTitle: "உதவி வேண்டுமா?",
    contactMsg: "எங்கள் வாடிக்கையாளர் ஆதரவு குழுவைத் தொடர்பு கொள்ளவும்",
    helpline: "உதவி எண்",
    email: "மின்னஞ்சல்",
    error_title: "ஏதோ தவறு ஏற்பட்டது",
    invalidLink: "தவறான விலைப்பட்டியல் இணைப்பு.",
    footer: "ஏர்ஃபீல்ட் எக்ஸ்பிரஸ் பிரைவேட் லிமிடெட்",
  },
  te: {
    taxInvoice: "పన్ను ఇన్వాయిస్",
    checking: "ఇన్వాయిస్ స్థితిని తనిఖీ చేస్తోంది",
    checkingMsg: "మీ ఇన్వాయిస్ స్థితిని తనిఖీ చేస్తున్నాం, దయచేసి వేచి ఉండండి…",
    expired_title: "లింక్ గడువు ముగిసింది",
    expired_msg:
      "ఈ ఇన్వాయిస్ లింక్ గడువు ముగిసింది మరియు ఇకపై చెల్లదు. కొత్త లింక్ లేదా ఏదైనా సహాయం కోసం దయచేసి మా కస్టమర్ సపోర్ట్ టీమ్‌ను సంప్రదించండి.",
    pending_title: "ప్రొఫార్మా ఇన్వాయిస్ సమీక్ష",
    pending_subtitle: "మీ ప్రొఫార్మా ఇన్వాయిస్ సమీక్ష కోసం సిద్ధంగా ఉంది",
    pending_warning:
      "మీరు తుది పన్ను ఇన్వాయిస్‌ను ఆమోదించి జనరేట్ చేసిన తర్వాత, ఎటువంటి మార్పులు లేదా సవరణలు / రద్దు చేయడం సాధ్యం కాదు. దయచేసి ముందుకు వెళ్ళే ముందు అన్ని షిప్‌మెంట్ వివరాలు, ఛార్జీలు మరియు బిల్లింగ్ సమాచారాన్ని నిర్ధారించుకోండి.",
    approveBtn: "ఆమోదించి పన్ను ఇన్వాయిస్ జనరేట్ చేయండి",
    generating: "మీ ఇన్వాయిస్ జనరేట్ అవుతోంది…",
    generatingMsg: "మీ తుది పన్ను ఇన్వాయిస్ తయారు చేస్తున్నాం, దయచేసి వేచి ఉండండి…",
    ready_title: "ఇన్వాయిస్ సిద్ధంగా ఉంది",
    ready_msg: "మీ పన్ను ఇన్వాయిస్ విజయవంతంగా జనరేట్ చేయబడింది.",
    invoiceNo: "ఇన్వాయిస్ నంబర్",
    downloadBtn: "ఇన్వాయిస్ డౌన్‌లోడ్ చేయండి",
    contactTitle: "సహాయం కావాలా?",
    contactMsg: "మా కస్టమర్ సపోర్ట్ టీమ్‌ను సంప్రదించండి",
    helpline: "హెల్ప్‌లైన్",
    email: "ఈమెయిల్",
    error_title: "ఏదో తప్పు జరిగింది",
    invalidLink: "చెల్లని ఇన్వాయిస్ లింక్.",
    footer: "ఎయిర్‌ఫీల్డ్ ఎక్స్‌ప్రెస్ ప్రైవేట్ లిమిటెడ్",
  },
};

const TaxInvoice: React.FC = () => {
  const { encrypted_awb } = useParams();
  const [lang, setLang] = useState<Lang>("en");
  const [loading, setLoading] = useState(true);
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatus>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [invoiceNo, setInvoiceNo] = useState<string | number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const l = t[lang];

  // Step 1: Check invoice status on mount
  useEffect(() => {
    if (!encrypted_awb) {
      setError(l.invalidLink);
      setLoading(false);
      return;
    }

    axios
      .get(`${BASE_URL}/tax-invoice-status/${encrypted_awb}`)
      .then((res: any) => {
        const d = res.data || res; // interceptor unwraps response.data, so actual payload is in res.data
        if (d.status === "ready") {
          setInvoiceStatus("ready");
          setPdfUrl(`${BASE_URL}${d.pdfPath}`);
          setInvoiceNo(d.invoice_no || null);
        } else if (d.status === "expired") {
          setInvoiceStatus("expired");
        } else if (d.status === "pending") {
          setInvoiceStatus("pending");
        } else {
          setError(d.message || "Unexpected response.");
        }
      })
      .catch((err) => {
        const msg = typeof err === "string" ? err : err?.message || "Something went wrong.";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [encrypted_awb]);

  // Step 2: Approve & Generate
  const handleApproveGenerate = () => {
    if (!encrypted_awb || generating) return;
    setGenerating(true);
    setError(null);

    axios
      .get(`${BASE_URL}/tax-invoice/${encrypted_awb}`)
      .then((res: any) => {
        const d = res.data || res; // interceptor unwraps response.data
        if (d.pdfPath) {
          setPdfUrl(`${BASE_URL}${d.pdfPath}`);
          setInvoiceNo(d.invoice_no || null);
          setInvoiceStatus("ready");
        } else {
          setError(d.message || res.message || "Unable to generate invoice.");
        }
      })
      .catch((err) => {
        const msg = typeof err === "string" ? err : err?.message || "Something went wrong.";
        setError(msg);
      })
      .finally(() => setGenerating(false));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-3 py-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo2.png" alt="Airfield Express" className="h-12 sm:h-7" />
            <div className="h-6 w-px bg-slate-300 sm:hidden" />
            <span className="text-sm font-medium text-slate-500 sm:hidden">{l.taxInvoice}</span>
          </div>
          <a
            href="https://www.airfield.co.in"
            target="_blank"
            rel="noreferrer"
            className="text-base text-blue-600 hover:text-blue-800 hover:underline transition-colors"
          >
            www.airfield.co.in
          </a>
        </div>
      </header>

      {/* Language Selector */}
      <div className="max-w-4xl mx-auto w-full px-4 sm:px-3 pt-5 sm:pt-3">
        <div className="flex items-center justify-center gap-1.5 flex-wrap">
          {langOptions.map((lo) => (
            <button
              key={lo.code}
              onClick={() => setLang(lo.code)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                lang === lo.code
                  ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-600"
              }`}
            >
              {lo.native}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-4 sm:px-3 py-8 sm:py-5">
        <div className="w-full max-w-md">

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 sm:p-6 text-center">
              <div className="mx-auto w-16 h-16 sm:w-14 sm:h-14 rounded-full bg-blue-50 flex items-center justify-center mb-5">
                <svg className="w-8 h-8 sm:w-7 sm:h-7 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-lg font-bold text-slate-800 mb-2">{l.checking}</h2>
              <p className="text-base sm:text-sm text-slate-500 leading-relaxed">{l.checkingMsg}</p>
              <div className="mt-5 flex justify-center gap-1.5">
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {/* Generating State */}
          {generating && (
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8 sm:p-6 text-center">
              <div className="mx-auto w-16 h-16 sm:w-14 sm:h-14 rounded-full bg-blue-50 flex items-center justify-center mb-5">
                <svg className="w-8 h-8 sm:w-7 sm:h-7 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-lg font-bold text-slate-800 mb-2">{l.generating}</h2>
              <p className="text-base sm:text-sm text-slate-500 leading-relaxed">{l.generatingMsg}</p>
              <div className="mt-5 flex justify-center gap-1.5">
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {/* Error State */}
          {!loading && !generating && error && (
            <div className="bg-white rounded-2xl shadow-xl border border-red-100 p-8 sm:p-6 text-center">
              <div className="mx-auto w-16 h-16 sm:w-14 sm:h-14 rounded-full bg-red-50 flex items-center justify-center mb-5">
                <svg className="w-8 h-8 sm:w-7 sm:h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-lg font-bold text-slate-800 mb-2">{l.error_title}</h2>
              <p className="text-base sm:text-sm text-slate-500 leading-relaxed">{error}</p>
            </div>
          )}

          {/* Expired State */}
          {!loading && !generating && !error && invoiceStatus === "expired" && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-8 sm:p-6 text-center">
                <div className="mx-auto w-16 h-16 sm:w-14 sm:h-14 rounded-full bg-orange-50 flex items-center justify-center mb-5">
                  <svg className="w-8 h-8 sm:w-7 sm:h-7 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl sm:text-lg font-bold text-slate-800 mb-3">{l.expired_title}</h2>
                <p className="text-base sm:text-sm text-slate-500 leading-relaxed mb-6">{l.expired_msg}</p>
                <div className="h-px bg-slate-100 mb-5" />
                <h3 className="text-base sm:text-sm font-semibold text-slate-700 mb-3">{l.contactTitle}</h3>
                <p className="text-sm sm:text-xs text-slate-500 mb-4">{l.contactMsg}</p>
                <div className="flex flex-col gap-2.5 items-center">
                  <a
                    href="tel:+919313022422"
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {l.helpline}: +91 93130 22422
                  </a>
                  <a
                    href="mailto:cs@airfield.co.in"
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {l.email}: cs@airfield.co.in
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Pending State — Approve & Generate */}
          {!loading && !generating && !error && invoiceStatus === "pending" && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl shadow-xl border border-amber-200 p-8 sm:p-6 text-center">
                <div className="mx-auto w-16 h-16 sm:w-14 sm:h-14 rounded-full bg-amber-50 flex items-center justify-center mb-5">
                  <svg className="w-8 h-8 sm:w-7 sm:h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-xl sm:text-lg font-bold text-slate-800 mb-1">{l.pending_title}</h2>
                <p className="text-base sm:text-sm text-slate-400 mb-5">{l.pending_subtitle}</p>

                {/* Warning box */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-3 mb-6 text-left">
                  <div className="flex gap-3 sm:gap-2">
                    <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-sm sm:text-xs text-amber-800 leading-relaxed">{l.pending_warning}</p>
                  </div>
                </div>

                <button
                  onClick={handleApproveGenerate}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold rounded-xl shadow-lg shadow-emerald-200 hover:shadow-xl hover:shadow-emerald-300 hover:from-emerald-700 hover:to-emerald-800 active:scale-[0.98] transition-all text-sm"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {l.approveBtn}
                </button>
              </div>
            </div>
          )}

          {/* Ready State — Download */}
          {!loading && !generating && !error && invoiceStatus === "ready" && pdfUrl && (
            <div className="bg-white rounded-2xl shadow-xl border border-green-200 p-8 sm:p-6 text-center">
              <div className="mx-auto w-16 h-16 sm:w-14 sm:h-14 rounded-full bg-green-50 flex items-center justify-center mb-5">
                <svg className="w-8 h-8 sm:w-7 sm:h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-lg font-bold text-slate-800 mb-2">{l.ready_title}</h2>
              <p className="text-base sm:text-sm text-slate-500 mb-2">{l.ready_msg}</p>
              {invoiceNo && (
                <p className="text-sm text-slate-400 mb-6">
                  {l.invoiceNo}: <span className="font-semibold text-slate-600">#{invoiceNo}</span>
                </p>
              )}
              {!invoiceNo && <div className="mb-6" />}
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-300 hover:from-blue-700 hover:to-blue-800 active:scale-[0.98] transition-all text-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {l.downloadBtn}
              </a>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs text-slate-400">
            {l.footer} &middot; cs@airfield.co.in &middot; +91 93130 22422
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TaxInvoice;
